/**
 * #132 (0101): "Minhas mesas" — o garçom declara as mesas que atende.
 * Chips das mesas ativas; toque alterna a claim. Com claims ativas, um
 * seletor filtra o salão para "só as minhas" (mesa a mesa no modo garçom;
 * no kanban o filtro é visual nas chamadas/contas, o fluxo de cozinha segue
 * completo). Sem claims: visão padrão do salão inteiro.
 */
import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tables as tablesApi } from '../lib/api.js'
import type { TableClaim } from '../lib/api.js'
import { getTokenPayload } from '../lib/auth.js'
import { useI18n } from '../lib/i18n.js'
import { cn } from '../lib/utils.js'

export function WaiterTableClaims({
  mineOnly,
  onMineOnlyChange,
}: {
  mineOnly: boolean
  onMineOnlyChange: (v: boolean) => void
}) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const me = getTokenPayload()?.sub ?? ''

  const { data: list = [] } = useQuery({ queryKey: ['tables'], queryFn: tablesApi.list })
  const { data: claims = [] } = useQuery({
    queryKey: ['table-claims'],
    queryFn: tablesApi.listClaims,
    refetchInterval: 20_000,
  })

  const activeTables = useMemo(
    () => list.filter((tb) => tb.is_active).sort((a, b) => a.number - b.number),
    [list],
  )
  const claimsByTable = useMemo(() => {
    const map = new Map<string, TableClaim[]>()
    for (const cl of claims) {
      const arr = map.get(cl.table_id) ?? []
      arr.push(cl)
      map.set(cl.table_id, arr)
    }
    return map
  }, [claims])
  const myTableIds = useMemo(
    () => new Set(claims.filter((cl) => cl.waiter_user_id === me).map((cl) => cl.table_id)),
    [claims, me],
  )

  const toggle = useMutation({
    mutationFn: ({ tableId, claim }: { tableId: string; claim: boolean }) =>
      claim ? tablesApi.claim(tableId) : tablesApi.releaseClaim(tableId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['table-claims'] }),
  })

  if (activeTables.length === 0) return null

  return (
    <div
      data-testid="waiter-my-tables"
      className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <p className="text-sm font-bold text-gray-900">{t('admin.waiter.my_tables')}</p>
          <p className="text-xs text-gray-500">{t('admin.waiter.my_tables_hint')}</p>
        </div>
        {myTableIds.size > 0 && (
          <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 shrink-0">
            <button
              data-testid="waiter-tables-filter-mine"
              data-active={mineOnly}
              onClick={() => onMineOnlyChange(true)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                mineOnly ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              {t('admin.waiter.my_tables_mine')}
            </button>
            <button
              data-testid="waiter-tables-filter-all"
              data-active={!mineOnly}
              onClick={() => onMineOnlyChange(false)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                !mineOnly ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              {t('admin.waiter.my_tables_all')}
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {activeTables.map((tb) => {
          const mine = myTableIds.has(tb.id)
          const others = (claimsByTable.get(tb.id) ?? []).filter((cl) => cl.waiter_user_id !== me)
          return (
            <button
              key={tb.id}
              data-testid={`waiter-table-claim-${tb.id}`}
              data-claimed={mine}
              disabled={toggle.isPending}
              onClick={() => toggle.mutate({ tableId: tb.id, claim: !mine })}
              title={others
                .map((o) => o.waiter_name)
                .filter(Boolean)
                .join(', ')}
              className={cn(
                'px-2.5 py-1 rounded-lg text-xs font-bold transition-colors',
                mine ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              )}
            >
              {tb.number}
              {mine ? ' ✓' : ''}
              {others.length > 0 ? ' +' : ''}
            </button>
          )
        })}
        {myTableIds.size === 0 && (
          <p className="text-xs text-gray-400 w-full pt-1">{t('admin.waiter.my_tables_none')}</p>
        )}
      </div>
    </div>
  )
}
